import { useState, useEffect } from 'react';
import { documentService } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from '../ui';
import { Download, X, Loader2, FileText, AlertCircle } from 'lucide-react';

/**
 * Componente per la preview dei documenti
 * Supporta: PDF, immagini (jpg, jpeg, png, gif)
 */
function DocumentPreview({ document, open, onClose }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tipi supportati per la preview
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif'];
  const pdfTypes = ['pdf'];

  const isImage = imageTypes.includes(document?.fileType);
  const isPdf = pdfTypes.includes(document?.fileType);
  const canPreview = isImage || isPdf;

  useEffect(() => {
    if (open && document && canPreview) {
      loadPreview();
    }

    return () => {
      // Cleanup URL quando il componente si smonta o il dialog si chiude
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [open, document?._id]);

  const loadPreview = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await documentService.download(document._id);
      const blob = new Blob([response.data], {
        type: getMimeType(document.fileType),
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      setError('Impossibile caricare l\'anteprima del documento');
      console.error('Errore preview:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getMimeType = (fileType) => {
    const mimeTypes = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
    };
    return mimeTypes[fileType] || 'application/octet-stream';
  };

  const handleDownload = async () => {
    try {
      const response = await documentService.download(document._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.fileName);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Errore download:', err);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setError(null);
    onClose();
  };

  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl sm:max-h-[90vh] flex flex-col h-[85dvh] sm:h-auto">
        <DialogHeader className="flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pr-8">
            <DialogTitle className="truncate text-base sm:text-lg">{document.title}</DialogTitle>
            <Button variant="outline" size="sm" onClick={handleDownload} className="w-full sm:w-auto min-h-[44px] touch-manipulation">
              <Download className="mr-2 h-4 w-4" />
              Scarica
            </Button>
          </div>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
            {document.fileName} • {document.fileSizeFormatted}
          </DialogDescription>
        </DialogHeader>

        <div 
          className="flex-1 min-h-0 overflow-hidden rounded-lg border border-border bg-muted/30"
          style={{
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full min-h-[250px] sm:min-h-[400px]">
              <div className="text-center">
                <Loader2 className="animate-spin text-primary mx-auto mb-4" size={32} />
                <p className="text-muted-foreground text-sm">Caricamento anteprima...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full min-h-[250px] sm:min-h-[400px] p-4">
              <div className="text-center">
                <AlertCircle className="text-destructive mx-auto mb-4" size={32} />
                <p className="text-destructive text-sm">{error}</p>
                <Button variant="outline" className="mt-4 min-h-[44px] touch-manipulation" onClick={loadPreview}>
                  Riprova
                </Button>
              </div>
            </div>
          ) : !canPreview ? (
            <div className="flex items-center justify-center h-full min-h-[250px] sm:min-h-[400px] p-4">
              <div className="text-center">
                <FileText className="text-muted-foreground mx-auto mb-4" size={48} />
                <p className="text-muted-foreground mb-2 text-sm">
                  Anteprima non disponibile per questo tipo di file
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                  Formato: {document.fileType.toUpperCase()}
                </p>
                <Button onClick={handleDownload} className="min-h-[44px] touch-manipulation">
                  <Download className="mr-2 h-4 w-4" />
                  Scarica per visualizzare
                </Button>
              </div>
            </div>
          ) : previewUrl && isImage ? (
            <div className="flex items-center justify-center h-full min-h-[250px] sm:min-h-[400px] p-2 sm:p-4 overflow-auto">
              <img
                src={previewUrl}
                alt={document.title}
                className="max-w-full max-h-full object-contain rounded"
                style={{
                  touchAction: 'pinch-zoom',
                }}
              />
            </div>
          ) : previewUrl && isPdf ? (
            <iframe
              src={previewUrl}
              title={document.title}
              className="w-full h-full min-h-[300px] sm:min-h-[500px]"
              style={{ border: 'none' }}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPreview;
